import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Button, Box } from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import CeroInput from "../../../components/CeroInput";
import CeroSelect from "../../../components/CeroSelect";
import { months, sampleYear } from "../../../constants";
import { addOffsetValidation } from "./schema";
import {
  addCarbonOffset,
  getCountryList,
  getPrograms,
  listCarbonOffsetPrograms,
  resetCarbonOffset,
  updateCarbonOffsetFormDetails,
} from "../../../redux/actions";
import { STATUS } from "../../../redux/constants";
import useStyles from "./styles";
import CeroPopUp from "../../../components/CeroPopUp";
import CeroButton from "../../../components/CeroButton";
import CeroAutoComplete from "../../../components/CeroAutoComplete";
import AttachmentOutlinedIcon from "@mui/icons-material/AttachmentOutlined";
import CeroUploadedFile from "../../../components/CeroUploadedFile";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSpeciesPlants,
  resetEditOffsetStatus,
} from "../../../redux/actions/carbonOffset";
import { getProgramTypes } from "../../../redux/actions/carbonOffset";
import { getCarbonOffsetVegetationDetails } from "../../../redux/actions/carbonOffset";
import { APIEndpoints } from "../../../redux/constants";
import { request } from "../../../services/client";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMemo } from "react";

const CreateCarbonOffsetPopup = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { isOpen, formDetails, isEdit } = props;
  const addCarbonOffsetStatus = useSelector(
    (state) => state.carbonOffset.addCarbonOffset.status
  );
  const editStatusMessage = useSelector(
    (state) => state.carbonOffset.updateCarbonOffsetFormDetails.message
  );
  const editCarbonOffsetStatus = useSelector(
    (state) => state.carbonOffset.updateCarbonOffsetFormDetails.status
  );
  const statusMessage = useSelector(
    (state) => state.carbonOffset.addCarbonOffset.message
  );
  const programTypes = useSelector(
    (state) => state.carbonOffset.getProgramTypes.data
  );
  const programs = useSelector((state) => state.carbonOffset.getPrograms.data);
  const countryListData = useSelector(
    (state) => state.listings.countryList.data
  );

  const vegetationRaw = useSelector(
    (s) => s.carbonOffset.getCarbonOffsetVegetationDetails?.data
  );

  // component
  const speciesRaw =
    useSelector((s) => s.carbonOffset.getSpeciesPlants?.data) || [];
  const speciesStatus = useSelector(
    (s) => s.carbonOffset.getSpeciesPlants?.status
  );

  const speciesList = Array.isArray(speciesRaw)
    ? speciesRaw
    : speciesRaw?.species_plants || [];

  const speciesOptions = speciesList.map((sp) => ({
    key: sp.id ?? sp._id,
    value: sp.name,
  }));

  const speciesDescMap = useMemo(() => {
    const map = new Map();
    speciesList.forEach((sp) => {
      if (sp?.name) map.set(sp.name, sp.description || "");
      if (sp?.id) map.set(sp.id, sp.description || "");
      if (sp?._id) map.set(sp._id, sp.description || "");
    });
    return map;
  }, [speciesList]);

  const vegetationList = Array.isArray(vegetationRaw)
    ? vegetationRaw
    : vegetationRaw?.vegetation_types || [];

  const vegetationTypeOptions = vegetationList.map((v) => ({
    key: v._id ?? v.id,
    value: v.name,
  }));

  const countryList = countryListData.map((item) => ({
    id: item.code,
    label: item.name,
  }));
  const countryFacilityData = useSelector(
    (state) => state.listings.getOrganizationCountry.data.country_facility_data
  );

  const facilityList = [];

  for (const country in countryFacilityData) {
    const facilities = countryFacilityData[country];
    for (const facility_name in facilities) {
      const facility_id = facilities[facility_name];
      facilityList.push({ id: facility_id, label: facility_name });
    }
  }
  const [selectedFacilitites, setFacility] = useState(facilityList);
  const programTypeOptions = programTypes.map((item) => ({
    key: item.id,
    value: item.name,
  }));
  const programOptions = [];
  const statusList = [];
  const nameMap = {};

  programs.forEach((item) => {
    programOptions.push({
      key: item.id,
      value: item.name,
    });

    if (item.status === false) {
      statusList.push(item.name);
    }
    nameMap[item.id] = item.name;
  });

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const yearList = sampleYear()
    .map((year) => ({
      ...year,
      disabled: year.key > currentYear,
    }))
    .filter((year) => !year.disabled);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [monthOptions, setMonthOptions] = useState(months);
  const isButtonLoading = addCarbonOffsetStatus === STATUS.RUNNING;

  const [calcLoading, setCalcLoading] = useState(false);
  const [calcValue, setCalcValue] = useState(null); // e.g., 123.45
  const [calcUnit, setCalcUnit] = useState(""); // e.g., "tCO2e/yr"

  const getAnnualFromPSD = (psd) => {
    if (!Array.isArray(psd)) return null;
    return (
      psd.find((x) =>
        /annual\s*carbon\s*sequestration/i.test(x?.data_name || "")
      ) || null
    );
  };

  const num = (x) =>
    x === "" || x === null || x === undefined ? undefined : Number(x);

  const [aoiManual, setAoiManual] = useState(false);

  const DBH_UNITS = [
    { key: "cm", value: "cm" },
    { key: "m", value: "m" },
  ];

  const AREA_UNITS = [
    { key: "Hectares", value: "Hectares" },
    { key: "Acres", value: "Acres" },
  ];

  const findValuelByLabel = (options, value) => {
    const option = options.find(
      (opt) => opt.value === value || opt.key === value
    );
    return option;
  };

  const initialProgram = findValuelByLabel(
    programOptions,
    formDetails?.program_name
  );

  const getProgramSpecificValue = (key) =>
    formDetails?.program_specific_data?.find((item) => item.data_name === key)
      ?.value || "";

  const addCarbonOffsetForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      program_name: formDetails?.name || "",
      vegetation_type: getProgramSpecificValue("Vegetation Type"),
      select_plant: getProgramSpecificValue("Select Plant") || "", ////

      country: formDetails?.country_code || "",
      program_type: formDetails?.program_type_id || "",
      program: initialProgram?.key || "",
      start_year: formDetails?.start_year || 0,
      start_month: formDetails?.start_month || 0,
      number_of_panels: getProgramSpecificValue("number of panels"),
      installed_capacity: getProgramSpecificValue("Installed capacity (KW)"),
      height_of_turbine: getProgramSpecificValue("Height of Turbines"),
      rotor_diameter: getProgramSpecificValue("Rotor diameter"),
      number_of_turbines: getProgramSpecificValue("No. of Turbines"),
      number_of_converters: getProgramSpecificValue("No. of Converters"),
      device_dimensions: getProgramSpecificValue("Device Dimensions"),
      trees_planted: getProgramSpecificValue("Number of Trees Planted"),
      climate_zone: getProgramSpecificValue("Climate Zone"),
      hydro_type: getProgramSpecificValue("Type of Hydro power"),
      wave_energy_converter_type: getProgramSpecificValue(
        "Type of Wave Energy Converter"
      ),
      total_area_planted: getProgramSpecificValue("Total Area Planted"),
      planting_density: getProgramSpecificValue("Planting Density"),
      description: formDetails?.description || "",
      facility: formDetails?.facility_id || "",
      dbh_value: getProgramSpecificValue(
        "Enter Average DBH (Diameter at breast height)"
      ),
      dbh_unit: getProgramSpecificValue("DBH Unit") || "cm",
      land_area_unit: getProgramSpecificValue("Land Area Unit") || "Hectares",
    },
    validationSchema: addOffsetValidation,
    onSubmit: () => {},
  });

  const selectedPlantValue = addCarbonOffsetForm.values.select_plant;
  const selectedPlantDescription =
    speciesDescMap.get(selectedPlantValue) ||
    speciesDescMap.get(
      speciesList.find((sp) => sp?.name === selectedPlantValue)?.id
    ) ||
    "Select a plant to see details.";

  const getChosenProgramName = () =>
    nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() || "";

  const isForestProgram = () => {
    const p = getChosenProgramName();
    return ["afforestation", "reforestation", "deforestation"].includes(p);
  };

  const canShowOtherFields =
    !isForestProgram() || !!addCarbonOffsetForm.values.vegetation_type;

  const hasVeg = !!addCarbonOffsetForm.values.vegetation_type;

  const programTypeName = (
    programTypes.find((pt) => pt.id === addCarbonOffsetForm.values.program_type)
      ?.name || ""
  ).toLowerCase();

  const isGreenCoverType = programTypeName === "green cover";

  const formValid = () => {
    const needsDescription = !isGreenCoverType;

    const base =
      addCarbonOffsetForm.values.program_name &&
      addCarbonOffsetForm.values.country &&
      addCarbonOffsetForm.values.facility &&
      addCarbonOffsetForm.values.program_type &&
      (needsDescription ? !!addCarbonOffsetForm.values.description : true);

    return isForestProgram()
      ? base && !!addCarbonOffsetForm.values.vegetation_type
      : base;
  };

  const updateSelectedFacilities = (country) => {
    const facilityList = [];
    const facilities = countryFacilityData[country];
    for (const facility_name in facilities) {
      const facility_id = facilities[facility_name];
      facilityList.push({ id: facility_id, label: facility_name });
    }
    setFacility(facilityList);
  };

  async function blobUrlToFile(blobUrl, filename) {
    try {
      const response = await fetch(blobUrl);

      if (!response.ok) {
        // throw new Error(Failed to fetch blob: ${response.statusText});
      }

      // Get the Blob object
      const blob = await response.blob();

      // Create a File object from the Blob
      const file = new File([blob], filename || "file", {
        type: blob.type,
      });
      return file;
    } catch (error) {
      console.error("Error converting blob URL to file:", error);
      return null;
    }
  }
  useEffect(() => {
    if (isOpen) {
      setAoiManual(false);
    }
  }, [isOpen]);
  useEffect(() => {
    if (isEdit && formDetails?.attachment_url?.length) {
      const fetchFiles = async () => {
        const files = await Promise.all(
          formDetails.attachment_url?.map((url, index) =>
            blobUrlToFile(url, `attachment-${index + 1}`)
          )
        );
        const validFiles = files?.filter((file) => file !== null); // Filter out failed conversions
        setSelectedFiles((prev) => [
          ...prev,
          ...validFiles?.map((file) => ({
            name: file.name,
            size: `${(file.size / 1024).toFixed(2)} KB`,
            file,
          })),
        ]);
      };
      fetchFiles();
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    const hit = getAnnualFromPSD(formDetails?.program_specific_data);
    if (hit) {
      const v = Number(hit.value);
      setCalcValue(Number.isFinite(v) ? v : null);
      setCalcUnit(hit.unit || "tons CO2/year");
    }
  }, [isEdit, formDetails]);

  useEffect(() => {
    if (isForestProgram()) {
      dispatch(getCarbonOffsetVegetationDetails());
    } else {
      addCarbonOffsetForm.setFieldValue("vegetation_type", "");
    }
  }, [dispatch, addCarbonOffsetForm.values.program]);

  useEffect(() => {
    const vegId = addCarbonOffsetForm.values.vegetation_type;
    if (vegId) {
      dispatch(getSpeciesPlants(vegId));
      addCarbonOffsetForm.setFieldValue("select_plant", "");
    } else {
      addCarbonOffsetForm.setFieldValue("select_plant", "");
    }
  }, [dispatch, addCarbonOffsetForm.values.vegetation_type]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      file,
    }));
    setSelectedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };

  const buildProgramSpecificData = (forCalc = false) => {
    const v = addCarbonOffsetForm.values;

    const num = (x: any) =>
      x === "" || x === null || x === undefined ? undefined : Number(x);

    // Backward-compat: treat old "Area" as "Acres"
    const unitRaw = v.land_area_unit === "Area" ? "Acres" : v.land_area_unit;

    const areaRaw = num(v.total_area_planted);
    const isAcres = unitRaw === "Acres";

    // Convert to hectares only for calculation payloads
    const areaForCalc =
      forCalc && areaRaw !== undefined && isAcres
        ? Number((areaRaw * 0.4046856422).toFixed(6)) // 1 acre = 0.4046856422 ha
        : areaRaw;

    const unitForCalc = forCalc ? "Hectares" : unitRaw;

    const list = [
      { data_name: "Vegetation Type", value: v.vegetation_type },
      { data_name: "Select Plant", value: v.select_plant },

      { data_name: "Enter Land Area", value: areaForCalc },
      { data_name: "Land Area Unit", value: unitForCalc },

      {
        data_name: "Enter Average DBH (Diameter at breast height)",
        value: num(v.dbh_value),
      },
      { data_name: "DBH Unit", value: v.dbh_unit },

      { data_name: "Number of trees per hectar", value: num(v.trees_planted) },

      { data_name: "Total Area Planted", value: num(v.total_area_planted) },
      { data_name: "number of panels", value: num(v.number_of_panels) },
      {
        data_name: "Installed capacity (KW)",
        value: num(v.installed_capacity),
      },
      { data_name: "Height of Turbines", value: num(v.height_of_turbine) },
      { data_name: "Rotor diameter", value: num(v.rotor_diameter) },
      { data_name: "No. of Turbines", value: num(v.number_of_turbines) },
      { data_name: "No. of Converters", value: num(v.number_of_converters) },
      { data_name: "Device Dimensions", value: num(v.device_dimensions) },
      { data_name: "Climate Zone", value: v.climate_zone },
      { data_name: "Type of Hydro power", value: v.hydro_type },
      {
        data_name: "Type of Wave Energy Converter",
        value: v.wave_energy_converter_type,
      },
    ];

    return list.filter(
      (i) => i.value !== undefined && i.value !== "" && i.value !== null
    );
  };

  const buildFormData = (isSave, forCalc = false) => {
    const fd = new FormData();
    fd.append("program_type_id", addCarbonOffsetForm.values.program_type);
    fd.append("name", addCarbonOffsetForm.values.program_name);
    fd.append("choose_program_id", addCarbonOffsetForm.values.program);
    fd.append("start_year", addCarbonOffsetForm.values.start_year);
    fd.append("start_month", addCarbonOffsetForm.values.start_month);
    fd.append("description", addCarbonOffsetForm.values.description || "");
    fd.append("country", addCarbonOffsetForm.values.country);
    fd.append("facility", addCarbonOffsetForm.values.facility);
    fd.append(
      "program_specific_data",
      JSON.stringify(buildProgramSpecificData(forCalc))
    );
    fd.append("save", forCalc ? "false" : "true");

    if (isSave) {
      selectedFiles.forEach((f) => fd.append("file", f.file));
    }
    return fd;
  };

  const canCalculate = () => {
    return (
      isForestProgram() &&
      !!addCarbonOffsetForm.values.vegetation_type &&
      !!addCarbonOffsetForm.values.select_plant &&
      !!addCarbonOffsetForm.values.total_area_planted &&
      !!addCarbonOffsetForm.values.land_area_unit &&
      !!addCarbonOffsetForm.values.dbh_value &&
      !!addCarbonOffsetForm.values.dbh_unit &&
      !!addCarbonOffsetForm.values.trees_planted
    );
  };

  const pickAnnualFrom = (r) => {
    const buckets = [
      r?.data?.program_specific_data,
      r?.program_specific_data,
      r?.data?.offset_program?.program_specific_data,
      r?.offset_program?.program_specific_data,
      r?.data,
    ].filter(Boolean);

    for (const psd of buckets) {
      if (Array.isArray(psd)) {
        const hit = psd.find((x) =>
          /annual\s*carbon\s*sequestration/i.test(x?.data_name || "")
        );
        if (hit) return hit;
      }
    }
    return null;
  };

  const fetchAnnualFromDetails = async () => {
    if (!isEdit || !id) return null;

    const details = await request(
      APIEndpoints.GET_CARBON_OFFSET_DETAILS.replace(":id", id),
      {
        method: "GET",
      }
    );
    return pickAnnualFrom(details);
  };

  const handleCalculate = async () => {
    try {
      setCalcLoading(true);
      setCalcValue(null);
      setCalcUnit("");

      const fd = buildFormData(false, true); // your current payload
      const res = await request(APIEndpoints.ADD_CARBON_OFFSET, {
        method: "POST",
        payload: fd,
        isFormData: true,
      });

      let computed = pickAnnualFrom(res);

      if (!computed) {
        const fromDetails = await fetchAnnualFromDetails();
        if (fromDetails) computed = fromDetails;
      }

      if (!computed) {
        console.log("Calc API raw response:", res);
        enqueueSnackbar(
          "Calculation didn’t return a value. Check field names/units.",
          { variant: "warning" }
        );
        return;
      }

      const v = Number(computed.value);
      setCalcValue(Number.isFinite(v) ? v : computed.value);
      setCalcUnit(computed.unit || "tCO2e/yr");
      enqueueSnackbar("Calculation completed.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(
        e?.message || "We couldn't calculate right now. Please try again.",
        { variant: "error" }
      );
    } finally {
      setCalcLoading(false);
    }
  };

  const onAddData = () => {
    const formData = buildFormData(true); // normal save

    if (isEdit) {
      dispatch(updateCarbonOffsetFormDetails(id, formData));
      navigate("/tickets");
    } else {
      dispatch(addCarbonOffset(formData));
      navigate("/carbon-offset");
    }
  };

  const onClose = useCallback(() => {
    addCarbonOffsetForm.resetForm({});
    props.onClose();
  }, [addCarbonOffsetForm, props]);

  useEffect(() => {
    addCarbonOffsetForm.values.program_type &&
      dispatch(getPrograms(addCarbonOffsetForm.values.program_type));
    formDetails?.program_type &&
      dispatch(getPrograms(formDetails?.program_type_id));
  }, [
    dispatch,
    addCarbonOffsetForm.values.program_type,
    formDetails?.program_type_id,
  ]);

  useEffect(() => {
    dispatch(getProgramTypes());
    dispatch(getCountryList());
    if (formDetails?.country_code) {
      addCarbonOffsetForm.setFieldValue("country", formDetails.country_code);
      updateSelectedFacilities(formDetails.country_code);
    }
  }, [dispatch, formDetails]);

  useEffect(() => {
    if (editCarbonOffsetStatus === STATUS.SUCCESS) {
      enqueueSnackbar("Data updated successfully", {
        variant: "success",
      });
      dispatch(listCarbonOffsetPrograms());
      dispatch(resetEditOffsetStatus());
      setSelectedFiles([]);
      onClose();
    } else if (editCarbonOffsetStatus === STATUS.ERROR) {
      enqueueSnackbar(
        editStatusMessage
          ? editStatusMessage
          : "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetEditOffsetStatus());
    }
  }, [addCarbonOffsetStatus, enqueueSnackbar, onClose, dispatch]);

  useEffect(() => {
    if (addCarbonOffsetStatus === STATUS.SUCCESS) {
      enqueueSnackbar("Data added successfully", {
        variant: "success",
      });
      dispatch(listCarbonOffsetPrograms());
      dispatch(resetCarbonOffset());
      setSelectedFiles([]);
      onClose();
    } else if (addCarbonOffsetStatus === STATUS.ERROR) {
      enqueueSnackbar(
        statusMessage
          ? statusMessage
          : "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetCarbonOffset());
    }
  }, [addCarbonOffsetStatus, enqueueSnackbar, onClose, dispatch]);

  const handleYearChange = (event) => {
    const selectedYear = event.target.value;
    addCarbonOffsetForm.setFieldValue("start_year", selectedYear);

    if (parseInt(selectedYear) === currentYear) {
      const availableMonths = months.filter(
        (month) => month.key <= currentMonth
      );
      setMonthOptions(availableMonths);
    } else {
      setMonthOptions(months);
    }
  };
  const selectedFacility = facilityList?.find(
    (facility) => facility?.id === addCarbonOffsetForm.values.facility
  );
  const selectedCountry = countryList?.find(
    (facility) => facility?.id === addCarbonOffsetForm.values.country
  );
  const getContent = () => {
    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CeroInput
              classes={{ container: classes.selectContainer }}
              required
              id="program_name"
              name="program_name"
              label="Program Name"
              fullWidth
              value={addCarbonOffsetForm.values.program_name}
              onChange={addCarbonOffsetForm.handleChange}
              onBlur={addCarbonOffsetForm.handleBlur}
              error={
                addCarbonOffsetForm.touched.program_name &&
                addCarbonOffsetForm.errors.program_name
              }
            />
            <CeroAutoComplete
              id="facility"
              label="Facility"
              required
              value={selectedFacility}
              onChange={(e, value) =>
                addCarbonOffsetForm.setFieldValue(
                  "facility",
                  value ? value.id : undefined
                )
              }
              onBlur={addCarbonOffsetForm.handleBlur}
              error={
                addCarbonOffsetForm.touched.facility &&
                addCarbonOffsetForm.errors.facility
              }
              options={selectedFacilitites}
              sortOption={true}
            />
            {addCarbonOffsetForm.values.program_type && (
              <CeroSelect
                classes={{ container: classes.selectContainer }}
                required
                id="program"
                name="program"
                label="Choose Program"
                fullWidth
                options={programOptions}
                selectedValue={addCarbonOffsetForm.values.program || ""}
                onChange={addCarbonOffsetForm.handleChange}
                onBlur={addCarbonOffsetForm.handleBlur}
                error={
                  addCarbonOffsetForm.touched.program &&
                  addCarbonOffsetForm.errors.program
                }
                disableItem={statusList}
              />
            )}

            {addCarbonOffsetForm.values.program && isForestProgram() && (
              <CeroSelect
                classes={{ container: classes.selectContainer }}
                required
                id="vegetation_type"
                name="vegetation_type"
                label="Vegetation type"
                fullWidth
                options={vegetationTypeOptions}
                selectedValue={addCarbonOffsetForm.values.vegetation_type || ""}
                onChange={addCarbonOffsetForm.handleChange}
                onBlur={addCarbonOffsetForm.handleBlur}
                error={
                  addCarbonOffsetForm.touched.vegetation_type &&
                  addCarbonOffsetForm.errors.vegetation_type
                }
              />
            )}

            {/* === AOI (Area of Interest) — Only "Enter Land Area Manually" === */}

            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              ["wind energy program", "solar energy program"].includes(
                nameMap[addCarbonOffsetForm.values.program]?.toLowerCase()
              ) && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="number_of_panels"
                  name="number_of_panels"
                  label="No. of panels installed"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.number_of_panels}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.number_of_panels &&
                    addCarbonOffsetForm.errors.number_of_panels
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "wind energy program" && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="height_of_turbine"
                  name="height_of_turbine"
                  label="Height of Turbines"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.height_of_turbine}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.height_of_turbine &&
                    addCarbonOffsetForm.errors.height_of_turbine
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              ["wind energy program", "hydro energy program"].includes(
                nameMap[addCarbonOffsetForm.values.program]?.toLowerCase()
              ) && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="rotor_diameter"
                  name="rotor_diameter"
                  label="Rotor Diameter"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.rotor_diameter}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.rotor_diameter &&
                    addCarbonOffsetForm.errors.rotor_diameter
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "hydro energy program" && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="number_of_turbines"
                  name="number_of_turbines"
                  label="No. of Turbines"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.number_of_turbines}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.number_of_turbines &&
                    addCarbonOffsetForm.errors.number_of_turbines
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "wave energy converter" && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="number_of_converters"
                  name="number_of_converters"
                  label="No. of Converters"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.number_of_converters}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.number_of_converters &&
                    addCarbonOffsetForm.errors.number_of_converters
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "wave energy converter" && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="device_dimensions"
                  name="device_dimensions"
                  label="Device Dimensions"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.device_dimensions}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.device_dimensions &&
                    addCarbonOffsetForm.errors.device_dimensions
                  }
                />
              )}

            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              !isGreenCoverType && (
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  id="start_month"
                  name="start_month"
                  label="Start Month"
                  fullWidth
                  options={monthOptions}
                  selectedValue={addCarbonOffsetForm.values.start_month || ""}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.start_month &&
                    addCarbonOffsetForm.errors.start_month
                  }
                />
              )}
          </Grid>
          <Grid item xs={6}>
            <CeroAutoComplete
              id="country"
              label="Country"
              required
              value={selectedCountry}
              onChange={(e, value) => {
                addCarbonOffsetForm.setFieldValue(
                  "country",
                  value ? value.id : undefined
                );
                updateSelectedFacilities(value ? value.id : undefined);
              }}
              onBlur={addCarbonOffsetForm.handleBlur}
              error={
                addCarbonOffsetForm.touched.country &&
                addCarbonOffsetForm.errors.country
              }
              options={countryList}
            />
            <CeroSelect
              classes={{ container: classes.selectContainer }}
              required
              id="program_type"
              name="program_type"
              label="Program Type"
              fullWidth
              options={programTypeOptions}
              selectedValue={addCarbonOffsetForm.values.program_type || ""}
              onChange={addCarbonOffsetForm.handleChange}
              onBlur={addCarbonOffsetForm.handleBlur}
              error={
                addCarbonOffsetForm.touched.program_type &&
                addCarbonOffsetForm.errors.program_type
              }
            />

            {addCarbonOffsetForm.values.program && canShowOtherFields && (
              <>
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  id="start_year"
                  name="start_year"
                  label="Start Year"
                  fullWidth
                  options={yearList}
                  selectedValue={addCarbonOffsetForm.values.start_year || ""}
                  onChange={handleYearChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.start_year &&
                    addCarbonOffsetForm.errors.start_year
                  }
                />
              </>
            )}

            {addCarbonOffsetForm.values.program &&
              isForestProgram() &&
              addCarbonOffsetForm.values.vegetation_type && (
                <div style={{ position: "relative" }}>
                  <CeroSelect
                    classes={{ container: classes.selectContainer }}
                    required
                    id="select_plant"
                    name="select_plant"
                    label="Select Plant"
                    fullWidth
                    options={speciesOptions}
                    selectedValue={
                      addCarbonOffsetForm.values.select_plant || ""
                    }
                    onChange={addCarbonOffsetForm.handleChange}
                    onBlur={addCarbonOffsetForm.handleBlur}
                    error={
                      addCarbonOffsetForm.touched.select_plant &&
                      addCarbonOffsetForm.errors.select_plant
                    }
                    disabled={speciesStatus === STATUS.RUNNING}
                    sx={{ pr: 6 }}
                  />
                </div>
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              [
                "solar energy program",
                "wind energy program",
                "hydro energy program",
                "wave energy converter",
              ].includes(
                nameMap[addCarbonOffsetForm.values.program]?.toLowerCase()
              ) && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="installed_capacity"
                  name="installed_capacity"
                  label="Installed Capacity (kW)"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.installed_capacity}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.installed_capacity &&
                    addCarbonOffsetForm.errors.installed_capacity
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "wind energy program" && (
                <CeroInput
                  classes={{ container: classes.selectContainer }}
                  required
                  id="number_of_turbines"
                  name="number_of_turbines"
                  label="No. of Turbines"
                  fullWidth
                  type="number"
                  value={addCarbonOffsetForm.values.number_of_turbines}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.number_of_turbines &&
                    addCarbonOffsetForm.errors.number_of_turbines
                  }
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "hydro energy program" && (
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  required
                  id="hydro_type"
                  name="hydro_type"
                  label="Type of Hydro power"
                  fullWidth
                  options={[
                    { key: "Run-of-River", value: "Run-of-River" },
                    { key: "Reservoir", value: "Reservoir" },
                    { key: "Pumped Storage", value: "Pumped Storage" },
                  ]}
                  selectedValue={addCarbonOffsetForm.values.hydro_type || ""}
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.hydro_type &&
                    addCarbonOffsetForm.errors.hydro_type
                  }
                  disableItem={statusList}
                />
              )}
            {addCarbonOffsetForm.values.program &&
              canShowOtherFields &&
              nameMap[addCarbonOffsetForm.values.program]?.toLowerCase() ===
                "wave energy program" && (
                <CeroSelect
                  classes={{ container: classes.selectContainer }}
                  required
                  id="wave_energy_converter_type"
                  name="wave_energy_converter_type"
                  label="Type of Wave Energy Converter"
                  fullWidth
                  options={[
                    { key: "Point Absorber", value: "Point Absorber" },
                    {
                      key: "Oscillating Water Column",
                      value: "Oscillating Water Column",
                    },
                    { key: "Oscillating Surge", value: "Oscillating Surge" },
                  ]}
                  selectedValue={
                    addCarbonOffsetForm.values.wave_energy_converter_type || ""
                  }
                  onChange={addCarbonOffsetForm.handleChange}
                  onBlur={addCarbonOffsetForm.handleBlur}
                  error={
                    addCarbonOffsetForm.touched.wave_energy_converter_type &&
                    addCarbonOffsetForm.errors.wave_energy_converter_type
                  }
                  disableItem={statusList}
                />
              )}
          </Grid>
          {/* ===== AOI row spans both columns ===== */}
          <Grid item xs={12}>
            {isForestProgram() && hasVeg && (
              <>
                <div style={{ fontWeight: 600, margin: "12px 0 8px" }}>
                  Area of Interest (AOI)
                </div>

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={aoiManual}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setAoiManual(on);
                      if (on) {
                        if (!addCarbonOffsetForm.values.land_area_unit) {
                          addCarbonOffsetForm.setFieldValue(
                            "land_area_unit",
                            "Hectares"
                          );
                        }
                      } else {
                        addCarbonOffsetForm.setFieldValue(
                          "total_area_planted",
                          ""
                        );
                        addCarbonOffsetForm.setFieldValue("land_area_unit", "");
                      }
                    }}
                  />
                  Enter Land Area Manually
                </label>

                {/* Show ONLY these two when checked */}
                {aoiManual && (
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <CeroInput
                        classes={{ container: classes.selectContainer }}
                        id="total_area_planted"
                        name="total_area_planted"
                        label="Enter Land Area"
                        fullWidth
                        type="number"
                        value={addCarbonOffsetForm.values.total_area_planted}
                        onChange={addCarbonOffsetForm.handleChange}
                        onBlur={addCarbonOffsetForm.handleBlur}
                        required
                        error={
                          addCarbonOffsetForm.touched.total_area_planted &&
                          addCarbonOffsetForm.errors.total_area_planted
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CeroSelect
                        classes={{ container: classes.selectContainer }}
                        id="land_area_unit"
                        name="land_area_unit"
                        label="Land Area Unit"
                        fullWidth
                        options={[
                          { key: "Hectares", value: "Hectares" },
                          { key: "Acres", value: "Acres" },
                        ]}
                        selectedValue={
                          addCarbonOffsetForm.values.land_area_unit || ""
                        }
                        onChange={addCarbonOffsetForm.handleChange}
                        onBlur={addCarbonOffsetForm.handleBlur}
                        required
                        error={
                          addCarbonOffsetForm.touched.land_area_unit &&
                          addCarbonOffsetForm.errors.land_area_unit
                        }
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Always visible (independent of checkbox) */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <CeroInput
                      classes={{ container: classes.selectContainer }}
                      id="dbh_value"
                      name="dbh_value"
                      label="Enter Average DBH (Diameter at breast height)"
                      fullWidth
                      type="number"
                      value={addCarbonOffsetForm.values.dbh_value || ""}
                      onChange={addCarbonOffsetForm.handleChange}
                      onBlur={addCarbonOffsetForm.handleBlur}
                      error={
                        addCarbonOffsetForm.touched.dbh_value &&
                        addCarbonOffsetForm.errors.dbh_value
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CeroSelect
                      classes={{ container: classes.selectContainer }}
                      id="dbh_unit"
                      name="dbh_unit"
                      label="Unit"
                      fullWidth
                      options={[
                        { key: "cm", value: "cm" },
                        { key: "m", value: "m" },
                      ]}
                      selectedValue={addCarbonOffsetForm.values.dbh_unit || ""}
                      onChange={addCarbonOffsetForm.handleChange}
                      onBlur={addCarbonOffsetForm.handleBlur}
                      error={
                        addCarbonOffsetForm.touched.dbh_unit &&
                        addCarbonOffsetForm.errors.dbh_unit
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div style={{ position: "relative" }}>
                      <CeroInput
                        classes={{ container: classes.selectContainer }}
                        required
                        id="trees_planted"
                        name="trees_planted"
                        label="Number of trees per hectare"
                        fullWidth
                        type="number"
                        value={addCarbonOffsetForm.values.trees_planted}
                        onChange={addCarbonOffsetForm.handleChange}
                        onBlur={addCarbonOffsetForm.handleBlur}
                        error={
                          addCarbonOffsetForm.touched.trees_planted &&
                          addCarbonOffsetForm.errors.trees_planted
                        }
                        sx={{ pr: 6 }}
                      />

                      <Tooltip
                        title={selectedPlantDescription}
                        arrow
                        enterDelay={300}
                        placement="left"
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#d32f2f",
                            cursor: "default",
                            zIndex: 2,
                            background: "white",
                          }}
                        />
                      </Tooltip>
                    </div>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        {addCarbonOffsetForm.values.program &&
          canShowOtherFields &&
          !isGreenCoverType && (
            <CeroInput
              classes={{ container: classes.selectContainer }}
              required
              id="description"
              name="description"
              label="Brief description about the project"
              fullWidth
              multiline
              rows="2"
              // type="number"
              value={addCarbonOffsetForm.values.description}
              onChange={addCarbonOffsetForm.handleChange}
              onBlur={addCarbonOffsetForm.handleBlur}
              error={
                addCarbonOffsetForm.touched.description &&
                addCarbonOffsetForm.errors.description
              }
            />
          )}
        {addCarbonOffsetForm.values.program && canShowOtherFields && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              className={classes.uploadButton}
              component="label"
              startIcon={<AttachmentOutlinedIcon />}
            >
              Attach Documents
              <input type="file" hidden multiple onChange={handleFileChange} />
            </Button>
            <Box sx={{ display: "flex", gap: "8px" }}>
              {selectedFiles?.map((file, index) => (
                <CeroUploadedFile
                  key={index}
                  fileName={file.name}
                  fileSize={file.size}
                  onRemove={() => handleRemoveFile(index)}
                />
              ))}
            </Box>
          </Grid>
        )}

        {isForestProgram() && hasVeg && (
          <>
            <Box sx={{ mt: 2 }}>
              <CeroButton
                buttonText={
                  calcLoading
                    ? "Calculating..."
                    : "Calculate Annual Carbon Sequestration"
                }
                variant="outlined"
                onClick={handleCalculate}
                disabled={!canCalculate() || calcLoading}
                className={`${classes.buttonSecondary} ${classes.calcButton}`}
              />
            </Box>

            {calcValue !== null && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  maxWidth: "fit-content",
                  overflowX: "auto",
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#333",
                    lineHeight: 1.2,
                  }}
                >
                  {new Intl.NumberFormat("en-IN", {
                    maximumFractionDigits: 2,
                  }).format(calcValue)}
                  {calcUnit ? (
                    <span style={{ fontSize: 13 }}> {calcUnit}</span>
                  ) : (
                    ""
                  )}
                </div>
              </Box>
            )}
          </>
        )}

        <Grid item xs={12}>
          <CeroButton
            buttonText={isButtonLoading ? "Saving..." : "Save"}
            variant="contained"
            onClick={onAddData}
            className={classes.buttonPrimary}
            disabled={!formValid()}
          />
          <CeroButton
            buttonText="Cancel"
            variant="outlined"
            disabled={false}
            onClick={onClose}
            className={classes.buttonSecondary}
          />
        </Grid>
      </>
    );
  };

  return (
    <CeroPopUp
      primaryPopUp={{
        open: isOpen,
        onClose: onClose,
        content: getContent(),
        header: { title: `${isEdit ? "Edit" : "Add"} Carbon Offset Programs` },
      }}
      dialog={classes.popUpClass}
      showCloseButton={true}
    />
  );
};

export default CreateCarbonOffsetPopup;
